import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Clock, Sparkles, Tag } from "lucide-react";
import { Link } from "react-router-dom";

import { Footer } from "../home/sections/homepage/footer";
import { blogPosts } from "./data";

export const BlogPage = () => {
  const featuredPost = blogPosts[0];

  return (
    <div className="min-h-screen bg-gradient-to-b from-warm-cream via-white to-sky-50">
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-100/50 via-transparent to-emerald-100/40" />
        <div className="absolute inset-0 opacity-40 blur-3xl">
          <div className="absolute top-10 left-10 w-64 h-64 bg-sky-200/60 rounded-full" />
          <div className="absolute bottom-0 right-16 w-72 h-72 bg-emerald-200/60 rounded-full" />
        </div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-white/60 text-sm font-medium text-gray-700 shadow-sm mb-6">
              <Sparkles className="w-4 h-4 text-sky-600" />
              Ideas for humans hiring with AI
            </div>
            <h1 className="font-noto-serif text-5xl md:text-6xl font-semibold text-gray-900 leading-tight mb-6">
              theResume Blog
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl">
              Stories about building a career in an AI-first world. Practical ways to show the real
              you, not just polished keywords.
            </p>
          </motion.div>

          <div className="mt-12 grid lg:grid-cols-5 gap-8 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-3 bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-sky-50" />
              <div className="relative p-8 md:p-10 space-y-4">
                <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-sky-700 bg-sky-50 px-3 py-1 rounded-full border border-sky-100">
                  Featured
                </div>
                <h2 className="font-noto-serif text-3xl md:text-4xl font-semibold text-gray-900 leading-snug">
                  {featuredPost.title}
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {featuredPost.description}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                  <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border border-gray-200 shadow-sm">
                    <Clock className="w-4 h-4 text-sky-600" />
                    <span>{featuredPost.readTime}</span>
                  </div>
                  <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border border-gray-200 shadow-sm">
                    <BookOpen className="w-4 h-4 text-emerald-600" />
                    <span>{featuredPost.publishedOn}</span>
                  </div>
                  {featuredPost.tags.slice(0, 2).map((tag) => (
                    <div
                      key={tag}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100"
                    >
                      <Tag className="w-4 h-4" />
                      <span>{tag}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to={`/blog/${featuredPost.slug}`}
                  className="inline-flex items-center gap-2 text-base font-semibold text-sky-700 hover:text-sky-900 transition-colors"
                >
                  Read the story
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="lg:col-span-2 bg-gray-900 text-white rounded-3xl overflow-hidden shadow-xl border border-gray-800"
            >
              <div className="p-8 space-y-4">
                <p className="text-sm uppercase tracking-[0.2em] text-gray-400">What to expect</p>
                <h3 className="font-noto-serif text-2xl font-semibold leading-snug">
                  Hiring with AI needs more signal than keywords.
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  We share frameworks, prompts, and honest lessons from building tools that show the
                  real person behind the resume.
                </p>
                <div className="flex flex-wrap gap-2">
                  {["AI interviews", "Storytelling", "Recruiter POV", "Job search"].map((chip) => (
                    <span
                      key={chip}
                      className="px-3 py-1 rounded-full bg-white/10 border border-white/10 text-sm text-white/90"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <BlogCard key={post.slug} post={post} index={index} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const BlogCard = ({ post, index }: { post: (typeof blogPosts)[number]; index: number }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05 * index }}
      className="group relative bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all overflow-hidden"
    >
      <div className={`h-2 w-full bg-gradient-to-r ${post.theme.accent}`} />
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 border border-sky-100">
            <BookOpen className="w-4 h-4" />
            {post.readTime}
          </span>
          <span>{post.publishedOn}</span>
        </div>
        <h3 className="font-noto-serif text-xl font-semibold text-gray-900 leading-snug group-hover:text-gray-950 transition-colors">
          {post.title}
        </h3>
        <p className="text-gray-600 leading-relaxed">{post.description}</p>
        <div className="flex flex-wrap gap-2">
          {post.tags.map((tag) => (
            <span
              key={tag}
              className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-700"
            >
              {tag}
            </span>
          ))}
        </div>
        <Link
          to={`/blog/${post.slug}`}
          className="inline-flex items-center gap-2 text-sm font-semibold text-sky-700 group-hover:text-sky-900"
        >
          Read more
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </motion.div>
  );
};
